import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SettingsIcon, SaveIcon, SmartphoneIcon, MailIcon, BanknoteIcon, BellIcon, TagIcon } from 'lucide-react';
import { useStore, AdminSettings as SettingsType } from '../../context/StoreContext';
export function AdminSettings() {
  const {
    adminSettings,
    updateAdminSettings
  } = useStore();
  const [form, setForm] = useState<SettingsType>(adminSettings);
  const [isSaved, setIsSaved] = useState(false);
  // Sync form if context changes externally
  useEffect(() => {
    setForm(adminSettings);
  }, [adminSettings]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateAdminSettings(form);
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 3000);
  };
  const ToggleSwitch = ({
    label,
    description,
    checked,
    onChange
  }: {
    label: string;
    description: string;
    checked: boolean;
    onChange: (val: boolean) => void;
  }) => <div className="flex items-start justify-between py-4 border-b border-slate-100 last:border-0" data-id="element-752">
      <div className="pr-4" data-id="element-753">
        <h4 className="text-sm font-semibold text-slate-900" data-id="element-754">{label}</h4>
        <p className="text-xs text-slate-500 mt-1" data-id="element-755">{description}</p>
      </div>
      <label className="flex items-center cursor-pointer flex-shrink-0 mt-1" data-id="element-756">
        <div className={`w-11 h-6 rounded-full transition-colors relative ${checked ? 'bg-sky-500' : 'bg-slate-300'}`} data-id="element-757">
          <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${checked ? 'left-6' : 'left-1'}`} data-id="element-758" />
        </div>
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="hidden" data-id="element-759" />
      </label>
    </div>;
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-760">
      <div className="flex items-center justify-between" data-id="element-761">
        <div data-id="element-762">
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-763">Store Settings</h1>
          <p className="text-sm text-slate-500" data-id="element-764">
            Manage your store configuration and preferences
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-765">
        {/* Contact & Payment Info */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-766">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2" data-id="element-767">
            <SettingsIcon className="w-5 h-5 text-slate-500" data-id="element-768" />
            <h2 className="font-semibold text-slate-900" data-id="element-769">
              General Information
            </h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-770">
            <div data-id="element-771">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-772">
                UPI ID for Payments
              </label>
              <div className="relative" data-id="element-773">
                <BanknoteIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-774" />
                <input type="text" value={form.upiId} onChange={e => setForm({
                ...form,
                upiId: e.target.value
              })} className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" placeholder="e.g. yourname@upi" data-id="element-775" />
              </div>
              <p className="text-xs text-slate-500 mt-1" data-id="element-776">
                Displayed to customers during UPI checkout
              </p>
            </div>

            <div data-id="element-777">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-778">
                Support Email
              </label>
              <div className="relative" data-id="element-779">
                <MailIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-780" />
                <input type="email" value={form.email} onChange={e => setForm({
                ...form,
                email: e.target.value
              })} className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" data-id="element-781" />
              </div>
            </div>

            <div data-id="element-782">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-783">
                Contact Mobile
              </label>
              <div className="relative" data-id="element-784">
                <SmartphoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-785" />
                <input type="tel" value={form.mobile} onChange={e => setForm({
                ...form,
                mobile: e.target.value
              })} className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" data-id="element-786" />
              </div>
            </div>

            <div data-id="element-787">
              <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-788">
                WhatsApp Number
              </label>
              <div className="relative" data-id="element-789">
                <SmartphoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-790" />
                <input type="tel" value={form.whatsappNumber} onChange={e => setForm({
                ...form,
                whatsappNumber: e.target.value
              })} className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500" placeholder="Include country code, e.g. 919876543210" data-id="element-791" />
              </div>
              <p className="text-xs text-slate-500 mt-1" data-id="element-792">
                Used for the floating WhatsApp button
              </p>
            </div>
          </div>
        </div>

        {/* Feature Toggles */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-793">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center gap-2" data-id="element-794">
            <TagIcon className="w-5 h-5 text-slate-500" data-id="element-795" />
            <h2 className="font-semibold text-slate-900" data-id="element-796">Feature Controls</h2>
          </div>
          <div className="px-6 py-2" data-id="element-797">
            <ToggleSwitch label="Cash on Delivery (COD)" description="Allow customers to pay using Cash on Delivery during checkout." checked={form.codEnabled} onChange={val => setForm({
            ...form,
            codEnabled: val
          })} data-id="element-798" />
            <ToggleSwitch label="Promo Codes System" description="Enable the promo code input field in the cart and checkout pages." checked={form.promoEnabled} onChange={val => setForm({
            ...form,
            promoEnabled: val
          })} data-id="element-799" />
            <ToggleSwitch label="Order Notifications" description="Send automated email/SMS notifications to customers when order status changes." checked={form.notificationEnabled} onChange={val => setForm({
            ...form,
            notificationEnabled: val
          })} data-id="element-800" />
          </div>
        </div>

        <div className="flex justify-end" data-id="element-801">
          <button type="submit" className="flex items-center gap-2 px-6 py-3 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-700 transition-colors shadow-sm" data-id="element-802">
            <SaveIcon className="w-5 h-5" data-id="element-803" />
            {isSaved ? 'Settings Saved!' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>;
}